import { useFieldArray, useFormContext, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import {
  Trash2,
  Plus,
  MapPin,
  Info,
  Calendar,
  Presentation,
  BarChart3,
  Mic,
  MessageCircle,
  Users,
  Briefcase,
  BookOpen,
  GraduationCap,
  Lightbulb,
  Coffee,
  Utensils,
  Wine,
  PartyPopper,
  Music,
  Award,
  Plane,
  Hotel,
  Car,
  Bus,
  Mountain,
  Sun,
  Dumbbell,
  Heart,
  type LucideIcon,
} from "lucide-react";
import { MuiColorInput } from "mui-color-input";
import {
  SESSION_TYPE_ICONS,
  DEFAULT_SESSION_TYPE_ICON,
} from "@event-planner/shared";
import type { EventFormData } from "../../schemas/eventSchema";
import { NamedAudienceSelect } from "../../components/NamedAudienceSelect";

/**
 * Map from curated icon-name → Lucide component. Mirrors the shared list
 * in `constants/sessionTypeIcons.ts`. When that list grows, add the import
 * + an entry here. Unknown names render `Calendar` as a safe fallback.
 */
const ADMIN_ICON_MAP: Record<string, LucideIcon> = {
  Calendar,
  Presentation,
  BarChart3,
  Mic,
  MessageCircle,
  Users,
  Briefcase,
  BookOpen,
  GraduationCap,
  Lightbulb,
  Coffee,
  Utensils,
  Wine,
  PartyPopper,
  Music,
  Award,
  Plane,
  Hotel,
  Car,
  Bus,
  MapPin,
  Mountain,
  Sun,
  Dumbbell,
  Heart,
};

function resolveIcon(name: string | null | undefined): LucideIcon {
  if (!name) return ADMIN_ICON_MAP[DEFAULT_SESSION_TYPE_ICON]!;
  return ADMIN_ICON_MAP[name] ?? ADMIN_ICON_MAP[DEFAULT_SESSION_TYPE_ICON]!;
}

interface SessionTypeManagerProps {
  /**
   * Event ID for the category editor. When undefined (create mode pre-save),
   * the per-category GroupMultiSelect renders disabled with a hint that
   * default audiences become editable after the event exists.
   */
  eventId: string | undefined;
}

/**
 * Small preview mirroring the mobile agenda list row: a circular icon
 * badge in the category color, then the category name + a sample time.
 * Keeps admin authoring honest about how the row renders on mobile.
 */
function CardPreview({
  typeName,
  backgroundColor,
  textColor,
  iconName,
}: {
  typeName: string;
  backgroundColor: string;
  textColor: string;
  iconName: string | null;
}) {
  const isValidHex = (c: string) => /^#[0-9A-Fa-f]{6}$/.test(c);
  const bg = isValidHex(backgroundColor) ? backgroundColor : "#9E9E9E";
  const fg = isValidHex(textColor) ? textColor : "#FFFFFF";
  const Icon = resolveIcon(iconName);

  return (
    <Box
      sx={{
        width: 240,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        py: 1,
        flexShrink: 0,
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon size={18} color={fg} strokeWidth={2.5} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{ fontWeight: 500, fontSize: "0.875rem", lineHeight: 1.3 }}
          noWrap
        >
          {typeName || "Session Title"} ›
        </Typography>
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "text.secondary",
            lineHeight: 1.2,
          }}
          noWrap
        >
          9:00 AM – 10:00 AM
        </Typography>
      </Box>
    </Box>
  );
}

export function SessionTypeManager({ eventId }: SessionTypeManagerProps) {
  const {
    control,
    register,
    formState: { errors },
    watch,
  } = useFormContext<EventFormData>();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "config.sessionTypes",
  });

  // Per ADR-120, role audiences don't exist until the event document
  // commits and `onEventCreatedBootstrapAudiences` fires. The default
  // audience picker is therefore disabled in create-mode (no eventId
  // yet) and enabled in edit-mode where the bootstrap has run.
  const eventSaved = Boolean(eventId);

  function handleAddType() {
    append({
      id: crypto.randomUUID(),
      name: "",
      backgroundColor: "#5C6BC0",
      textColor: "#FFFFFF",
      iconName: DEFAULT_SESSION_TYPE_ICON,
      defaultAudienceId: null,
    });
  }

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <Button
          onClick={handleAddType}
          startIcon={<Plus size={20} />}
          variant="outlined"
        >
          Add Category
        </Button>
        <Tooltip
          title="Categories set the color and label for agenda items. They can also save a default audience, which pre-fills the session audience when organizers create new sessions."
          arrow
          placement="top"
          slotProps={{ tooltip: { sx: { fontSize: "1rem", maxWidth: 360, p: 1.5 } } }}
        >
          <IconButton aria-label="Category default audience info">
            <Info size={22} />
          </IconButton>
        </Tooltip>
      </Box>

      {fields.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No categories defined. Add at least one.
        </Typography>
      )}

      {errors.config?.sessionTypes?.root && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {errors.config.sessionTypes.root.message}
        </Typography>
      )}

      {fields.map((field, index) => {
        const bgValue = watch(`config.sessionTypes.${index}.backgroundColor`);
        const fgValue = watch(`config.sessionTypes.${index}.textColor`);
        const nameValue = watch(`config.sessionTypes.${index}.name`);
        const iconValue = watch(`config.sessionTypes.${index}.iconName`);

        return (
          <Paper
            key={field.id}
            variant="outlined"
            sx={{ p: 2, mb: 2 }}
          >
            {/* Row 1: Name + Delete */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                mb: 2,
              }}
            >
              <TextField
                {...register(`config.sessionTypes.${index}.name`)}
                label="Category Name"
                required
                fullWidth
                error={!!errors.config?.sessionTypes?.[index]?.name}
                helperText={errors.config?.sessionTypes?.[index]?.name?.message}
              />
              <IconButton
                onClick={() => remove(index)}
                aria-label={`Remove ${field.name || "category"}`}
                color="error"
                sx={{ mt: 0.5 }}
              >
                <Trash2 size={20} />
              </IconButton>
            </Box>

            {/* Row 2: Color pickers + Live preview */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                mb: 2,
                flexWrap: "wrap",
              }}
            >
              <Controller
                name={`config.sessionTypes.${index}.backgroundColor`}
                control={control}
                render={({ field: colorField }) => (
                  <MuiColorInput
                    {...colorField}
                    label="Background"
                    format="hex"
                    sx={{ width: 180 }}
                    error={!!errors.config?.sessionTypes?.[index]?.backgroundColor}
                    helperText={errors.config?.sessionTypes?.[index]?.backgroundColor?.message}
                  />
                )}
              />
              <Controller
                name={`config.sessionTypes.${index}.textColor`}
                control={control}
                render={({ field: colorField }) => (
                  <MuiColorInput
                    {...colorField}
                    label="Text"
                    format="hex"
                    sx={{ width: 180 }}
                    error={!!errors.config?.sessionTypes?.[index]?.textColor}
                    helperText={errors.config?.sessionTypes?.[index]?.textColor?.message}
                  />
                )}
              />
              <Controller
                name={`config.sessionTypes.${index}.iconName`}
                control={control}
                render={({ field: iconField }) => (
                  <TextField
                    select
                    label="Icon"
                    value={iconField.value ?? DEFAULT_SESSION_TYPE_ICON}
                    onChange={(e) => iconField.onChange(e.target.value)}
                    sx={{ width: 220 }}
                    SelectProps={{
                      renderValue: (selected) => {
                        const Icon = resolveIcon(selected as string);
                        const entry = SESSION_TYPE_ICONS.find(
                          (i) => i.name === selected,
                        );
                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Icon size={18} />
                            <span>{entry?.label ?? "Calendar (default)"}</span>
                          </Box>
                        );
                      },
                    }}
                  >
                    {SESSION_TYPE_ICONS.map((icon) => {
                      const Icon = resolveIcon(icon.name);
                      return (
                        <MenuItem key={icon.name} value={icon.name}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.25,
                            }}
                          >
                            <Icon size={18} />
                            <span>{icon.label}</span>
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </TextField>
                )}
              />
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  Preview
                </Typography>
                <CardPreview
                  typeName={nameValue || "Session Title"}
                  backgroundColor={bgValue ?? "#5C6BC0"}
                  textColor={fgValue ?? "#FFFFFF"}
                  iconName={iconValue ?? null}
                />
              </Box>
            </Box>

            {/* Row 3: Default audience for new sessions in this category */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Default audience for new sessions in this category
                {!eventSaved &&
                  " — editable after the event is created."}
              </Typography>
              <Controller
                name={`config.sessionTypes.${index}.defaultAudienceId`}
                control={control}
                render={({ field: audField }) => (
                  <NamedAudienceSelect
                    eventId={eventId}
                    value={audField.value ?? null}
                    onChange={audField.onChange}
                    label="Default audience"
                    placeholder={
                      eventSaved
                        ? "Pick an audience"
                        : "Editable after create"
                    }
                    helperText="New sessions in this category start with this audience. Existing sessions are unaffected by changes here."
                    disabled={!eventSaved}
                    testId={`session-type-default-audience-${index}`}
                  />
                )}
              />
            </Box>

          </Paper>
        );
      })}

    </Box>
  );
}
